import React, { useEffect, useState, useRef, useLayoutEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import './AssetSummaryDetails.css';
import { getAccountsLite, getSitesLite, getPlantsBySite, getAssetSummary, getAssetDetails, getAssetAlerts, AssetDetailsResponse, AssetAttribute, AssetAlert, postStatusQuery, StatusQueryRequest, StatusQueryResponse, GetDocumentByAssetID, UploadDocumentByAssetID, DeleteDocumentByID, DownloadDocumentByID, CustomerAccountLite, parsePaginatedSummaryResponse } from '../../Services/serviceURL';
import apiInstance from '../../config/HttpCommon';
import { CustomerLite } from '../Plants/CreatePlant';
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import * as am5radar from "@amcharts/amcharts5/radar";
import am5themes_Dark from "@amcharts/amcharts5/themes/Dark";
import AnalysisTab from './AnalysisTab';
import { createAmChartsRoot } from '../../utils/amChartsConfig';
import RemoteAccess from './RemoteAccess';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Loader from '../../Components/common/loader/loader';
import { decodeToken } from '../../Services/Users.service';

interface AssetSummaryDetailsProps {
  assetId?: string;
}

// Use the imported types from serviceURL
type AssetDetails = AssetDetailsResponse;
   // Unified time formatting function (same as in Summary component)
  const formatDateTime = (dateString: string | null | undefined): string => {
  if (!dateString) return 'N/A';
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    
    return date.toLocaleString('en-US', {
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  } catch (error) {
    return dateString;
  }
};
const AssetSummaryDetails: React.FC<AssetSummaryDetailsProps> = ({ assetId: propAssetId }) => {
  const { assetId: urlAssetId } = useParams<{ assetId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const assetId = propAssetId || urlAssetId;

  // Utility function to format attribute values
  const formatAttributeValue = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return 'N/A';

    // Format numbers with appropriate precision
    if (Number.isInteger(value)) {
      return value.toString();
    } else {
      return value.toFixed(2); // Show 2 decimal places for non-integers
    }
  };
  const [activeTab, setActiveTab] = useState<string>('summary');
  const [selectedCustomer, setSelectedCustomer] = useState<string>('');
  const [selectedSite, setSelectedSite] = useState<string>('');
  const [customers, setCustomers] = useState<CustomerLite[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');
  const [sites, setSites] = useState<{ siteId: string; site: string }[]>([]);
  const [selectedSiteId, setSelectedSiteId] = useState<string>('');
  const [plants, setPlants] = useState<{ plantId: string; plant: string }[]>([]);
  const [selectedPlantId, setSelectedPlantId] = useState<string>('');
  // Searchable dropdown UI state
  const [remoteKey, setRemoteKey] = useState(0);

  const [isCustomerOpen, setIsCustomerOpen] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [isSiteOpen, setIsSiteOpen] = useState(false);
  const [siteSearch, setSiteSearch] = useState('');
  const [isPlantOpen, setIsPlantOpen] = useState(false);
  const [plantSearch, setPlantSearch] = useState('');
  const [isAssetOpen, setIsAssetOpen] = useState(false);
  const [assetSearch, setAssetSearch] = useState('');
  const [assets, setAssets] = useState<{ id: string; assetId: string; assetName: string }[]>([]);
  const [selectedAssetId, setSelectedAssetId] = useState<string>('');
  const [selectedAssetRecordId, setSelectedAssetRecordId] = useState<string>(''); // The 'id' field for API calls
  const [assetDetails, setAssetDetails] = useState<AssetDetails | null>(null);
  const [loadingAssetDetails, setLoadingAssetDetails] = useState<boolean>(false);
  const [lastCommunication, setLastCommunication] = useState<string | null>(null);

  // Upload modal state
  const [showUploadModal, setShowUploadModal] = useState<boolean>(false);
  const [documentTitle, setDocumentTitle] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState<boolean>(false);

  const [openDropdownId, setOpenDropdownId] = useState<number | null>(null); // Track which dropdown is open

  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [documentToDelete, setDocumentToDelete] = useState<number | null>(null);

  // Overwrite confirmation modal state
  const [showOverwriteModal, setShowOverwriteModal] = useState<boolean>(false);
  const [duplicateDocumentInfo, setDuplicateDocumentInfo] = useState<{ id: number; name: string } | null>(null);
  const [pendingUploadData, setPendingUploadData] = useState<{ file: File; title: string } | null>(null);
  const analysisFirstLoadRef = useRef(false);
  const [triggerAnalysisInitialLoad, setTriggerAnalysisInitialLoad] = useState(false);

  // Documents state (replace static array with state)
  const [documents, setDocuments] = useState<{ id: number; name: string; date: string }[]>([]);
  const [loadingDocuments, setLoadingDocuments] = useState<boolean>(false);
  
  // Extract user role from token (same pattern as Sidebar component)
  const extractUserInfoFromToken = () => {
    const accessToken = localStorage.getItem('access_token');
    if (accessToken) {
        try {
            const decoded = decodeToken(accessToken) as any;
            
            const userInfo = {
                email: decoded?.['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'],
                name: decoded?.['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'],
                role: decoded?.['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'],
                userType: decoded?.UserType,
                userId: decoded?.UserId
            };
            
            return userInfo;
        } catch (error) {
            return null;
        }
    }
    return null;
  };
  
  const tokenUserInfo = extractUserInfoFromToken();
  
  // Get user role from multiple sources (same pattern as Sidebar component)
  const rawRole = tokenUserInfo?.role || localStorage.getItem('userRole') || "";
  const normalizedRole = rawRole ? rawRole.toLowerCase().replace(/\s+/g, '') : '';
  const isAdmin = normalizedRole === "admin" || normalizedRole === "globaladmin";
  

  const canViewConnectivityTrend = isAdmin;

  const allTabs = [
    { id: 'summary', label: 'Summary' },
    { id: 'analysis', label: 'Analysis' },
    { id: 'documents', label: 'Documents' },
    { id: 'remote', label: 'Remote' },
  ];

  // Filter tabs based on user role - show Remote tab only for admin/globaladmin users
  const tabs = allTabs.filter(tab => 
    tab.id !== 'remote' || isAdmin
  );


  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        // Fetch customers using new GetAccountsLite API endpoint
        const customerAccountsData = await getAccountsLite();
        
        // Ensure we have an array to work with
        if (!Array.isArray(customerAccountsData)) {
          setCustomers([]);
          return;
        }
        
        // Transform the API response to match CustomerLite interface
        const validCustomers = customerAccountsData.map((customer: CustomerAccountLite, index: number) => ({
          id: customer.id || `customer-${index}`,
          customerId: customer.customerId,
          customerName: customer.customerName,
          soldTo: customer.salesDistrictCustomer || customer.salesDistrict || '', // Use salesDistrictCustomer as soldTo fallback
          sites: [] // Sites will be fetched separately when customer is selected
        }));
        
        setCustomers(validCustomers);
      } catch (error) {
        setCustomers([]);
      }
    };
    fetchInitialData();
  }, []);

  useEffect(() => {
    // Fetch sites when selectedCustomerId changes
    const fetchSites = async () => {
      if (!selectedCustomerId) {
        setSites([]);
        return;
      }
      try {
        const response = await getSitesLite(selectedCustomerId);
        const siteList = response && response.Data && Array.isArray(response.Data)
          ? response.Data.map((site: any) => ({ siteId: site.siteId, site: site.site }))
          : [];
        setSites(siteList);
      } catch (err) {
        setSites([]);
      }
    };
    fetchSites();
    // Only clear plants if not coming from query params
    if (!searchParams.get('customerId')) {
      setPlants([]);
      setSelectedPlantId('');
      // Don't clear assets here as they will be fetched based on customer
    }
  }, [selectedCustomerId, searchParams]);



  useEffect(() => {
    // Fetch plants when selectedSiteId changes
    const fetchPlants = async () => {
      if (!selectedSiteId) return;
      try {
        const response = await getPlantsBySite(selectedSiteId);
        const plantList = response && response.data && Array.isArray(response.data.Data)
          ? response.data.Data.map((plant: any) => ({ plantId: plant.plantId, plant: plant.plantName }))
          : [];
        setPlants(plantList);
      } catch (err) {
        setPlants([]);
      }
    };
    fetchPlants();
  }, [selectedSiteId]);


  useEffect(() => {
    // Fetch assets when selectedCustomerId and selectedSiteId changes
    const fetchAssets = async () => {
      if (!selectedCustomerId || !selectedSiteId || !selectedPlantId) {
        setAssets([]);
        return;
      }
      try {
         await fetchAssetsForPlant(selectedPlantId);     
        //   ? response.data.Data.map((asset: any) => ({
        //   : [];
       
      } catch (err) {
        setAssets([]);
      }
    };
    if (selectedPlantId) {
    fetchAssets();}
    else {
      setAssets([])
    }
  }, [selectedPlantId]);

  // Fetch asset details when selectedAssetRecordId changes
  useEffect(() => {

    const fetchAssetDetails = async () => {
      if (!selectedAssetRecordId) {
        setAssetDetails(null);
        setLastCommunication(null);
        return;
      }

      setLoadingAssetDetails(true);
      try {
        // Call the API endpoint: GET /api/Asset/GetDetails/{id}
        const details = await getAssetDetails(selectedAssetRecordId);
        setAssetDetails(details);
        setLastCommunication(details.lastCommunication || null);

        // Update the navigation state with the asset name for breadcrumb
        if (details && details.assetName) {
          const currentState = window.history.state || {};
          const newState = {
            ...currentState,
            assetName: details.assetName,
            breadcrumbTitle: details.assetName
          };
          window.history.replaceState(newState, '', window.location.pathname + window.location.search);
        }
      } catch (error) {
        setAssetDetails(null);
        setLastCommunication(null);
      } finally {
        setLoadingAssetDetails(false);
      }
    };

    fetchAssetDetails();
  }, [selectedAssetRecordId]);

  // Handler for customer dropdown change
  const handleCustomerChange = (newCustomerId: string) => {
      // If same customer is clicked again, force refresh
  if (selectedCustomerId === newCustomerId) {
    
    // Force clear everything to trigger refresh
    setSites([]);
    setPlants([]);
    setAssets([]);
    setSelectedSiteId('');
    setSelectedPlantId('');
    setSelectedAssetId('');
    setSelectedAssetRecordId('');
    setAssetDetails(null);
    
    // Clear search terms
    setSiteSearch('');
    setPlantSearch('');
    setAssetSearch('');
    
  
    
    // Force re-fetch sites for current customer
    fetchSitesForCustomer(newCustomerId);
    return;
  }
    setSelectedCustomerId(newCustomerId);

    // Reset all dependent dropdowns and clear asset details
    setSelectedSiteId('');
    setSelectedPlantId('');
    setSelectedAssetId('');
    setSelectedAssetRecordId('');
    setAssetDetails(null); // Clear asset details to show N/A values

    // Clear dependent data arrays
    setSites([]);
    setPlants([]);
    setAssets([]);
  };
  // Separate function to fetch sites
const fetchSitesForCustomer = async (customerId: string) => {
  try {
    
    const response = await getSitesLite(customerId);
    
    // Parse sites data with multiple format checks
    let siteList: Array<{ siteId: string; site: string }> = [];
    
    if (Array.isArray(response)) {
      // Format 1: Direct array
      siteList = response.map((site: any) => ({
        siteId: site.siteId || site.id || site.SiteID || String(Math.random()),
        site: site.site || site.siteName || site.SiteName || 'Unnamed Site'
      }));
    } else if (response && response.Data && Array.isArray(response.Data)) {
      // Format 2: { Data: [] }
      siteList = response.Data.map((site: any) => ({
        siteId: site.siteId || site.id || site.SiteID || String(Math.random()),
        site: site.site || site.siteName || site.SiteName || 'Unnamed Site'
      }));
    } else if (response && response.data && Array.isArray(response.data)) {
      // Format 3: { data: [] }
      siteList = response.data.map((site: any) => ({
        siteId: site.siteId || site.id || site.SiteID || String(Math.random()),
        site: site.site || site.siteName || site.SiteName || 'Unnamed Site'
      }));
    } else if (response && response.value && Array.isArray(response.value)) {
      // Format 4: { value: [] }
      siteList = response.value.map((site: any) => ({
        siteId: site.siteId || site.id || site.SiteID || String(Math.random()),
        site: site.site || site.siteName || site.SiteName || 'Unnamed Site'
      }));
    }
    
    setSites(siteList);
    
  } catch (error: any) {
    setSites([]);
  }
};
// Add fetchPlantsForSite function if not already there
const fetchPlantsForSite = async (siteId: string) => {
  try {
    const response = await getPlantsBySite(siteId);
    
    let plantList: Array<{ plantId: string; plant: string }> = [];
    
    if (Array.isArray(response)) {
      plantList = response.map((plant: any) => ({
        plantId: plant.plantId || plant.id || plant.PlantID || String(Math.random()),
        plant: plant.plant || plant.plantName || plant.PlantName || 'Unnamed Plant'
      }));
    } else if (response && response.data && response.data.Data && Array.isArray(response.data.Data)) {
      plantList = response.data.Data.map((plant: any) => ({
        plantId: plant.plantId || plant.id || plant.PlantID || String(Math.random()),
        plant: plant.plant || plant.plantName || plant.PlantName || 'Unnamed Plant'
      }));
    } else if (response && response.data.Data && Array.isArray(response.data.Data)) {
      plantList = response.data.Data.map((plant: any) => ({
        plantId: plant.plantId || plant.id || plant.PlantID || String(Math.random()),
        plant: plant.plant || plant.plantName || plant.PlantName || 'Unnamed Plant'
      }));
    }
    
    setPlants(plantList);
    
  } catch (error: any) {
    setPlants([]);
  }
};
// Add this function after fetchPlantsForSite
const fetchAssetsForPlant = async (plantId: string) => {
  try {
    
    // The API might expect different parameters for fetching assets by plant
    // You need to check the actual API endpoint requirements
    
    // Option 1: If the API supports plantId parameter
    const response = await getAssetSummary(1, 500, selectedCustomerId, selectedSiteId, plantId);
    const parsed = parsePaginatedSummaryResponse(response.data);
    const assetList: Array<{ id: string; assetId: string; assetName: string }> = parsed.items.map((asset, index) => ({
      id: asset.id || asset.assetId || `asset-${index}`,
      assetId: asset.assetId || asset.id || `ASSET-${index}`,
      assetName: asset.assetName || 'Unnamed Asset'
    }));

    setAssets(assetList);
    
  } catch (error: any) {
    setAssets([]); // Set empty array on error
  }
};
  // Filtered lists for searchable dropdowns

  const filteredCustomersList = useMemo(() => {

    const list = customers || [];

    const normalize = (s: any) => String(s ?? '').toLowerCase().replace(/\s+/g, ' ').trim();

    const term = normalize(customerSearch);

    if (!term) return list;

    return list.filter(c => {

      const name = normalize(c.customerName);

      const soldTo = normalize(c.soldTo);

      if (name === term || soldTo === term) return true;

      return name.includes(term) || soldTo.includes(term);

    });

  }, [customers, customerSearch]);



  const filteredSitesList = useMemo(() => {

    const list = sites || [];

    const normalize = (s: any) => String(s ?? '').toLowerCase().replace(/\s+/g, ' ').trim();

    const term = normalize(siteSearch);

    if (!term) return list;

    return list.filter(s => {

      const siteName = normalize(s.site);

      const siteId = normalize(s.siteId);

      if (siteName === term || siteId === term) return true;

      return siteName.includes(term) || siteId.includes(term);

    });

  }, [sites, siteSearch]);



  const filteredPlantsList = useMemo(() => {

    const list = plants || [];

    const normalize = (s: any) => String(s ?? '').toLowerCase().replace(/\s+/g, ' ').trim();

    const term = normalize(plantSearch);

    if (!term) return list;

    return list.filter(p => {

      const plantName = normalize(p.plant);

      const plantId = normalize(p.plantId);

      if (plantName === term || plantId === term) return true;

      return plantName.includes(term) || plantId.includes(term);

    });

  }, [plants, plantSearch]);



  const filteredAssetsList = useMemo(() => {

    const list = assets || [];

    const normalize = (s: any) => String(s ?? '').toLowerCase().replace(/\s+/g, ' ').trim();

    const term = normalize(assetSearch);

    if (!term) return list;

    return list.filter(a => {

      const assetName = normalize(a.assetName);

      const assetId = normalize(a.assetId);

      if (assetName === term || assetId === term) return true;

      return assetName.includes(term) || assetId.includes(term);

    });

  }, [assets, assetSearch]);

  //get document by asset id 
  const GetDocument = async () => {
    // Use selectedAssetId from dropdown if available, otherwise fall back to assetId from URL/props
    const assetIdToUse = selectedAssetId || assetId;
    
    if (!assetIdToUse) {
      setDocuments([]);
      setLoadingDocuments(false);
      return;
    }

    setLoadingDocuments(true);
    try {
      let assetData: any = await GetDocumentByAssetID(assetIdToUse);
      
      // Handle case where API returns empty array or null
      if (!assetData || !Array.isArray(assetData)) {
        setDocuments([]);
        setLoadingDocuments(false);
        return;
      }

      const documentData = assetData.map((asset: any, index: number) => {
        // Format date with time - Day with ordinal, Month, Time (e.g., "15th Oct, 14:32")
        let formattedDate = '';
        if (asset.uploadedAt) {
          const date = new Date(asset.uploadedAt);
          const day = date.getDate();
          // Add ordinal suffix (st, nd, rd, th)
          const getOrdinalSuffix = (n: number): string => {
            const j = n % 10;
            const k = n % 100;
            if (j === 1 && k !== 11) return 'st';
            if (j === 2 && k !== 12) return 'nd';
            if (j === 3 && k !== 13) return 'rd';
            return 'th';
          };
          const dayWithOrdinal = `${day}${getOrdinalSuffix(day)}`;
          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          const month = monthNames[date.getMonth()];
          // Format time as HH:MM (24-hour format)
          const hours = date.getHours().toString().padStart(2, '0');
          const minutes = date.getMinutes().toString().padStart(2, '0');
          const time = `${hours}:${minutes}`;
          formattedDate = `${dayWithOrdinal} ${month}, ${time}`;
        }
        return {
          id: asset.id,
          name: asset.documentName,
          date: formattedDate
        }
      });
      setDocuments(documentData);
    } catch (error) {
      setDocuments([]);
      toast.error('Failed to load documents');
    } finally {
      setLoadingDocuments(false);
    }
  }

  // Handle overwrite confirmation - Yes
  const handleConfirmOverwrite = async () => {
    if (!pendingUploadData || !duplicateDocumentInfo) {
      setShowOverwriteModal(false);
      return;
    }

    try {
      // Delete the existing duplicate document
      const deleteResponse = await DeleteDocumentByID([duplicateDocumentInfo.id.toString()]);
      if (deleteResponse.StatusCode === 200) {
        // Upload the new document
        let files: File[] = [];
        files.push(pendingUploadData.file);

        const response = await UploadDocumentByAssetID(selectedAssetRecordId, files, pendingUploadData.title);
        if (response.StatusCode == 200) {
          toast.success("File uploaded successfully and replaced existing document");
          GetDocument();
          handleCloseModal();
        } else {
          toast.error(response.Message || "Failed to upload file");
        }
      } else {
        toast.error("Failed to delete existing document");
      }
    } catch (error) {
      toast.error("An error occurred while overwriting the document");
    } finally {
      setShowOverwriteModal(false);
      setDuplicateDocumentInfo(null);
      setPendingUploadData(null);
    }
  };

  // Handle overwrite confirmation - No
  const handleCancelOverwrite = () => {
    toast.info("Upload cancelled");
    setShowOverwriteModal(false);
    setDuplicateDocumentInfo(null);
    setPendingUploadData(null);
  };
      const FORBIDDEN_EXTENSIONS = [
      'exe', 'msi', 'bat', 'cmd',
      'php', 'php3', 'php4', 'phtml',
      'js', 'sh', 'py',
      'dll', 'jar'
    ];

    const isFileAllowed = (file: File): boolean => {
      const extension = file.name.split('.').pop()?.toLowerCase();
      if (!extension) return false;

      return !FORBIDDEN_EXTENSIONS.includes(extension);
    };

  //get document by asset id 
  const SaveDocument = async () => {
    if (!selectedFile) {
      toast.error("Please select a file to upload");
      return;
    }
      
    if (!isFileAllowed(selectedFile)) {
      toast.error("This file type is not Supported");
      return;
    }
    if (!documentTitle || !documentTitle.trim()) {
      toast.error("Please enter a document title");
      return;
    }

    if (!selectedAssetRecordId) {
      toast.error("Please select an asset first");
      return;
    }

    try {
      // Get the asset ID to use for fetching documents
      const assetIdToUse = selectedAssetId || assetId;
      
      if (!assetIdToUse) {
        toast.error("Asset ID is required to check for duplicates");
        return;
      }

      // Use the user-entered document title
      const titleToUse = documentTitle.trim();

      // Fetch existing documents from backend to check for duplicates
      let existingDocuments: any[] = [];
      try {
        const existingDocsData = await GetDocumentByAssetID(assetIdToUse);
        if (existingDocsData && Array.isArray(existingDocsData)) {
          existingDocuments = existingDocsData;
        }
      } catch (error) {
        // Continue with upload even if we can't fetch existing documents
      }

      // Check for duplicate document name based on user-entered title (case-insensitive comparison)
      const duplicateDocument = existingDocuments.find(
        (doc: any) => doc.documentName && 
        doc.documentName.toLowerCase().trim() === titleToUse.toLowerCase().trim()
      );

      // If duplicate exists, show confirmation modal
      if (duplicateDocument && duplicateDocument.id) {
        toast.warning("A document with this name already exists");
        setDuplicateDocumentInfo({
          id: duplicateDocument.id,
          name: duplicateDocument.documentName
        });
        setPendingUploadData({
          file: selectedFile,
          title: titleToUse
        });
        setShowOverwriteModal(true);
        return;
      }

      // No duplicate, proceed with upload
      let files: File[] = [];
      files.push(selectedFile);

      const response = await UploadDocumentByAssetID(selectedAssetRecordId, files, titleToUse);
      if (response.StatusCode == 200) {
        toast.success("File uploaded successfully");
        GetDocument();
        handleCloseModal();
      } else {
        toast.error(response.Message || "Failed to upload file");
      }
    } catch (error) {
      toast.error("An error occurred while uploading the document");
    }
  }
  // Close any open dropdown when clicking outside

  useEffect(() => {

    const handleClickOutside = (e: MouseEvent) => {

      const target = e.target as Element | null;

      if (!target) return;

      const inDropdown = target.closest('.searchable-select-container');

      if (!inDropdown) {

        setIsCustomerOpen(false);

        setIsSiteOpen(false);

        setIsPlantOpen(false);

        setIsAssetOpen(false);

      }

    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);

  }, []);

  // Handler for drag and drop in modal
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
        if (!isFileAllowed(file)) {
        toast.error("This file type is not Supported");
        return;
      }
      setSelectedFile(file);
      // Automatically set document title to file name
      setDocumentTitle(file.name);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      setSelectedFile(file);
      // Automatically set document title to file name
      setDocumentTitle(file.name);
    }
  };

  const handleCloseModal = () => {
    setShowUploadModal(false);
    setDocumentTitle('');
    setSelectedFile(null);
    setIsDragging(false);
  };

  const handleUploadSubmit = () => {
    if (!selectedFile || !documentTitle.trim()) {
      toast.error('Please enter a document title and select a file');
      return;
    }

    // Handle file upload logic here

    // You can call your API here to upload the file
    // After successful upload, close modal and refresh document list
    handleCloseModal();
  };

  // Handle dropdown toggle
  const handleToggleDropdown = (documentId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    setOpenDropdownId(openDropdownId === documentId ? null : documentId);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!(event.target as Element).closest('.document-dropdown-container')) {
        setOpenDropdownId(null);
      }
    };

    if (openDropdownId !== null) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [openDropdownId]);

  // Handle delete from dropdown
  const handleDeleteFromDropdown = (documentId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    setOpenDropdownId(null);
    setDocumentToDelete(documentId);
    setShowDeleteModal(true);
  };

  // Handle delete confirmation
  const handleConfirmDelete = async () => {
    if (documentToDelete !== null) {
      let documentId: string[] = [];
      documentId.push(documentToDelete.toString());
      const response = await DeleteDocumentByID(documentId);
      if (response.StatusCode == 200) {
        GetDocument();
        toast.success("Document deleted successfully");
        handleCloseDeleteModal();
      } else {
        toast.error(response.Message || "Failed to delete document");
      }
    }
  };

  // Close delete confirmation modal
  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setDocumentToDelete(null);
  };

  //download document
  // Handle download from dropdown
const handleDownloadFromDropdown = async (
  documentId: number,
  event: React.MouseEvent
) => {
  event.stopPropagation();
  setOpenDropdownId(null);

  try {
    // Convert documentId to string array as expected by the API
    const documentIds: string[] = [documentId.toString()];
    const response = await DownloadDocumentByID(documentIds);
    if (response && response.StatusCode === 200) {
      toast.success("Document downloaded successfully");
    } else {
      toast.error(response?.Message || "Failed to download document");
    }
  } catch (error) {
    toast.error("Error while downloading document");
  }
};

  // Read query parameters and set initial values
  useEffect(() => {
    const customerId = searchParams.get('customerId');
    const siteId = searchParams.get('siteId');
    const plantId = searchParams.get('plantId');
    const selectedAsset = searchParams.get('selectedAssetId');

    if (customerId) {
      setSelectedCustomerId(customerId);
    }
    if (siteId) {
      setSelectedSiteId(siteId);
    }
    if (plantId) {
      setSelectedPlantId(plantId);
    }
    if (selectedAsset) {
      setSelectedAssetId(selectedAsset);
    }
  }, [searchParams]);

  // When assets are loaded and we have a selectedAssetId from URL, find the corresponding record ID
  useEffect(() => {
    if (selectedAssetId && assets.length > 0) {
      const selectedAsset = assets.find(asset => asset.assetId === selectedAssetId);
      if (selectedAsset) {
        setSelectedAssetRecordId(selectedAsset.id);
      }
    }
  }, [selectedAssetId, assets]);

  // Automatically reload documents when asset selection changes
  useEffect(() => {
    // Only reload documents if we have a valid asset ID
    if (selectedAssetId || assetId) {
      // Fetch documents for the selected asset
      GetDocument();
    } else {
      // Clear documents if no asset is selected
      setDocuments([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAssetId, assetId]); // Watch for changes in asset selection

 const OnclickTab = (tabid: string) => {
  // Reuse the already extracted role variables from component level

  
  // Prevent non-admin users from accessing remote tab
  if (tabid === 'remote' && !isAdmin) {

    return; // Don't allow tab switch
  }


  setActiveTab(tabid);

  if (tabid === "analysis") {
    if (!analysisFirstLoadRef.current) {
      analysisFirstLoadRef.current = true;
      setTriggerAnalysisInitialLoad(true); 
    }
  }

  if (tabid === "documents") {
    GetDocument();
  }

  if (tabid === "remote") {
    setRemoteKey(prev => prev + 1);
  }
};

 return (
    <div className="asset-summary-details">
      {/* Dropdown Controls */}

      <div className="asset-details-filters">

        <div className="filter-dropdowns">

          {/* Customer searchable dropdown */}

          <div className="searchable-select-container" style={{ display: 'inline-block', minWidth: '220px' }}>

            <div className="searchable-select-input" onClick={() => { const next = !isCustomerOpen; setIsCustomerOpen(next); setIsSiteOpen(false); setIsPlantOpen(false);setIsAssetOpen(false);  }}>

              <input

                type="text"

                value={isCustomerOpen ? customerSearch : (selectedCustomerId ? (customers.find(c => c.customerId === selectedCustomerId)?.customerName || '') : '')}

                onChange={(e) => setCustomerSearch(e.target.value)}

                onFocus={() => { /* no-op */ }}

                onKeyDown={(e) => {

                  if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {

                    setIsCustomerOpen(true);

                  }

                }}

                placeholder={isCustomerOpen ? 'Search customers...' : (selectedCustomerId ? (customers.find(c => c.customerId === selectedCustomerId)?.customerName || '') : 'All Customer')}

                className="asset-summary-select searchable-input"

                readOnly={!isCustomerOpen}

              />

              <span className={`dropdown-arrow assetsumary-arrow ${isCustomerOpen ? 'open' : ''}`}><svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M4.16797 7.5L10.0013 13.3333L15.8346 7.5" stroke="#FCFCFD" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                           </svg></span>

            </div>

            {isCustomerOpen && (

              <div className="searchable-dropdown">

                <div

                  className={`dropdown-option ${selectedCustomerId === '' ? 'selected' : ''}`}

                  onClick={() => { handleCustomerChange(''); setIsCustomerOpen(false); setCustomerSearch(''); }}

                >

                  All Customer

                </div>

                {filteredCustomersList.map((customer, index) => {

                  const label = `${customer.customerName}${customer.soldTo ? ` (${customer.soldTo})` : ''}`;

                  return (

                    <div

                      key={customer.id || `${customer.customerId}-${index}`}

                      className={`dropdown-option ${customer.customerId === selectedCustomerId ? 'selected' : ''}`}

                      onClick={() => { handleCustomerChange(customer.customerId); setIsCustomerOpen(false); setCustomerSearch(''); }}

                    >

                      {label}

                    </div>

                  );

                })}

              </div>

            )}

          </div>

          <span className="summary-separator">\</span>



          {/* Site searchable dropdown */}

          <div className="searchable-select-container" style={{ display: 'inline-block', minWidth: '200px' }}>

            <div className="searchable-select-input" onClick={() => { const next = !isSiteOpen; setIsSiteOpen(next); setIsCustomerOpen(false); setIsPlantOpen(false);setIsAssetOpen(false);  }}>

              <input

                type="text"

                value={isSiteOpen ? siteSearch : (selectedSiteId ? (sites.find(s => s.siteId === selectedSiteId)?.site || '') : '')}

                onChange={(e) => setSiteSearch(e.target.value)}

                onFocus={() => { /* no-op */ }}

                onKeyDown={(e) => {

                  if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {

                    setIsSiteOpen(true);

                  }

                }}

                placeholder={isSiteOpen ? 'Search sites...' : (selectedSiteId ? (sites.find(s => s.siteId === selectedSiteId)?.site || '') : 'All Sites')}

                className="asset-summary-select searchable-input"

                readOnly={!isSiteOpen}

              />

              <span className={`dropdown-arrow assetsumary-arrow ${isSiteOpen ? 'open' : ''}`}><svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M4.16797 7.5L10.0013 13.3333L15.8346 7.5" stroke="#FCFCFD" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                           </svg></span>

            </div>

            {isSiteOpen && (

              <div className="searchable-dropdown">

                <div

                  className={`dropdown-option ${selectedSiteId === '' ? 'selected' : ''}`}

                  onClick={() => {

                    const newSiteId = '';

                    setSelectedSiteId(newSiteId);

                    setSelectedPlantId('');

                    setSelectedAssetId('');

                    setSelectedAssetRecordId('');

                    setAssetDetails(null);

                    setPlants([]);

                    setAssets([]);

                    setIsSiteOpen(false);

                    setSiteSearch('');

                  }}

                >

                  All Sites

                </div>

                {filteredSitesList.map((site, index) => (

                  <div

                    key={`${site.siteId}-${index}`}

                    className={`dropdown-option ${site.siteId === selectedSiteId ? 'selected' : ''}`}

                    onClick={() => {
                    const newSiteId = site.siteId;
                    if (selectedSiteId == newSiteId ) {
                    // Force clear everything to trigger refresh
                    setPlants([]);
                    setAssets([]);
                    setSelectedPlantId('');
                    setSelectedAssetId('');
                    setSelectedAssetRecordId('');
                    setAssetDetails(null);
                    setIsSiteOpen(false);
                    // Clear search terms
                    setPlantSearch('');
                    setAssetSearch('');
                    
                    fetchPlantsForSite(newSiteId);
                    return;
                  }
                      

                      setSelectedSiteId(newSiteId);

                      setSelectedPlantId('');

                      setSelectedAssetId('');

                      setSelectedAssetRecordId('');

                      setAssetDetails(null);

                      setPlants([]);

                      setAssets([]);

                      setIsSiteOpen(false);

                      setSiteSearch('');

                    }}

                  >

                    {site.site}

                  </div>

                ))}

              </div>

            )}

          </div>

          <span className="summary-separator">\</span>



          {/* Plant searchable dropdown */}

          <div className="searchable-select-container" style={{ display: 'inline-block', minWidth: '200px' }}>

            <div className="searchable-select-input" onClick={() => { const next = !isPlantOpen; setIsPlantOpen(next); setIsCustomerOpen(false); setIsSiteOpen(false);setIsAssetOpen(false); }}>

              <input

                type="text"

                value={isPlantOpen ? plantSearch : (selectedPlantId ? (plants.find(p => p.plantId === selectedPlantId)?.plant || '') : '')}

                onChange={(e) => setPlantSearch(e.target.value)}

                onFocus={() => { /* no-op */ }}

                onKeyDown={(e) => {

                  if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {

                    setIsPlantOpen(true);

                  }

                }}

                placeholder={isPlantOpen ? 'Search plants...' : (selectedPlantId ? (plants.find(p => p.plantId === selectedPlantId)?.plant || '') : 'All Plants')}

                className="asset-summary-select searchable-input"

                readOnly={!isPlantOpen}

              />

              <span className={`dropdown-arrow assetsumary-arrow ${isPlantOpen ? 'open' : ''}`}><svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M4.16797 7.5L10.0013 13.3333L15.8346 7.5" stroke="#FCFCFD" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                           </svg></span>

            </div>

            {isPlantOpen && (

              <div className="searchable-dropdown">

                <div

                  className={`dropdown-option ${selectedPlantId === '' ? 'selected' : ''}`}

                  onClick={() => {

                    const newPlantId = '';
                    
                      
                    setSelectedPlantId(newPlantId);

                    setSelectedAssetId('');

                    setSelectedAssetRecordId('');

                    setAssetDetails(null);

                    setAssets([]);

                    setIsPlantOpen(false);

                    setPlantSearch('');

                  }}

                >

                  All Plants

                </div>

                {filteredPlantsList.map((plant, index) => (

                  <div

                    key={`${plant.plantId}-${index}`}

                    className={`dropdown-option ${plant.plantId === selectedPlantId ? 'selected' : ''}`}

                   onClick={() => {
                  const newPlantId = plant.plantId;
                  
                  if (selectedPlantId === newPlantId) {
                    
                    // Force clear everything to trigger refresh
                    setSelectedAssetId('');
                    setSelectedAssetRecordId('');
                    setAssetDetails(null);
                    
                    // Clear search terms
                    setAssetSearch('');
                    
                    // Fetch assets for the current plant again
                    fetchAssetsForPlant(newPlantId);
                    setSelectedPlantId(newPlantId); // Set to same value to trigger refresh
                  } else {
                    // Different plant selected - normal flow
                    setSelectedPlantId(newPlantId);
                    setSelectedAssetId('');
                    setSelectedAssetRecordId('');
                    setAssetDetails(null);
                    
                    // Fetch assets for the new plant
                    fetchAssetsForPlant(newPlantId);
                  }
                  
                  setIsPlantOpen(false);
                  setPlantSearch('');
                }}

                  >

                    {plant.plant}

                  </div>

                ))}

              </div>

            )}

          </div>

          <span className="summary-separator">\</span>



          {/* Asset searchable dropdown */}

          <div className="searchable-select-container" style={{ display: 'inline-block', minWidth: '200px' }}>

            <div className="searchable-select-input" onClick={() => { const next = !isAssetOpen; setIsAssetOpen(next); setIsCustomerOpen(false); setIsSiteOpen(false); setIsPlantOpen(false); }}>

              <input

                type="text"

                value={isAssetOpen ? assetSearch : (selectedAssetId ? (assets.find(a => a.assetId === selectedAssetId)?.assetName || '') : '')}

                onChange={(e) => setAssetSearch(e.target.value)}

                onFocus={() => { /* no-op */ }}

                onKeyDown={(e) => {

                  if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {

                    setIsAssetOpen(true);

                  }

                }}

                placeholder={isAssetOpen ? 'Search assets...' : (selectedAssetId ? (assets.find(a => a.assetId === selectedAssetId)?.assetName || '') : 'Select Asset')}

                className="asset-summary-select searchable-input"

                readOnly={!isAssetOpen}

              />

              <span className={`dropdown-arrow assetsumary-arrow ${isAssetOpen ? 'open' : ''}`}><svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M4.16797 7.5L10.0013 13.3333L15.8346 7.5" stroke="#FCFCFD" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                           </svg></span>

            </div>

            {isAssetOpen && (

              <div className="searchable-dropdown">

                <div

                  className={`dropdown-option ${selectedAssetId === '' ? 'selected' : ''}`}

                  onClick={() => {

                    setSelectedAssetId('');

                    setSelectedAssetRecordId('');

                    setIsAssetOpen(false);

                    setAssetSearch('');

                  }}

                >

                  Select Asset

                </div>

                {filteredAssetsList.map((asset, index) => (

                  <div

                    key={`${asset.id}-${index}`}

                    className={`dropdown-option ${asset.assetId === selectedAssetId ? 'selected' : ''}`}

                    onClick={() => {

                      const newAssetId = asset.assetId;

                      setSelectedAssetId(newAssetId);



                      // Find the corresponding asset record to get the 'id' field

                      const selectedAsset = assets.find(a => a.assetId === newAssetId);

                      if (selectedAsset) {

                        setSelectedAssetRecordId(selectedAsset.id);



                        // Update URL to reflect the new asset selection

                        const params = new URLSearchParams();

                        if (selectedCustomerId) params.append('customerId', selectedCustomerId);

                        if (selectedSiteId) params.append('siteId', selectedSiteId);

                        if (selectedPlantId) params.append('plantId', selectedPlantId);

                        params.append('selectedAssetId', newAssetId);



                        const queryString = params.toString();

                        const url = queryString ? `/asset-details/${newAssetId}?${queryString}` : `/asset-details/${newAssetId}`;

                        navigate(url, {

                          replace: true,

                          state: {

                            assetName: selectedAsset.assetName,

                            breadcrumbTitle: selectedAsset.assetName

                          }

                        });

                      } else {

                        setSelectedAssetRecordId('');

                      }

                      setIsAssetOpen(false);

                      setAssetSearch('');

                    }}

                  >

                    {asset.assetName}

                  </div>

                ))}

              </div>

            )}
          </div>
        </div>
      </div>

      <div className="tabs-container">
        <div className="tabs-header">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => OnclickTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
       <div className="tab-content-wrapper">

  {/* ================= SUMMARY TAB ================= */}
  <div
    className="tab-content"
    style={{ display: activeTab === 'summary' ? 'block' : 'none' }}
  >
    <div className={`widgets-container ${!canViewConnectivityTrend ? "no-connectivity" : ""}`}>
      <InfoWidget assetDetails={assetDetails} loading={loadingAssetDetails} />

      <AssetPropertiesWidget
        assetDetails={assetDetails}
        loading={loadingAssetDetails}
        formatAttributeValue={formatAttributeValue}
        lastCommunication={lastCommunication}
      />

      <SensorStatusWidget
        assetDetails={assetDetails}
        loading={loadingAssetDetails}
      />

      <AlertsWidget
        assetId={selectedAssetRecordId}
        assetDetails={assetDetails}
        loading={loadingAssetDetails}
      />

    {canViewConnectivityTrend && (
    <ConnectivityTrendWidget
      assetId={selectedAssetId}
      assetDetails={assetDetails}
    />
    )}
    </div>
  </div>

  {/* ================= ANALYSIS TAB ================= */}
  <div
    className="tab-content"
    style={{ display: activeTab === 'analysis' ? 'block' : 'none' }}
  >
    <AnalysisTab
      assetId={assetId}
      assetType={assetDetails?.assetType}
      assetParameters={assetDetails?.attributes}
      triggerInitialLoad={triggerAnalysisInitialLoad}
    />
  </div>

  {/* ================= ALERTS TAB ================= */}
  <div
    className="tab-content"
    style={{ display: activeTab === 'alerts' ? 'block' : 'none' }}
  >
    <div className="alerts-content">
      <h3>Active Alerts</h3>
    </div>
  </div>

  {/* ================= DOCUMENTS TAB ================= */}
  <div
    className="tab-content documents-content"
    style={{ display: activeTab === 'documents' ? 'block' : 'none' }}
  >
    <div className="documents-wrapper">

      <div className="documents-header">
        <h3 className="documents-title">Upload Documents</h3>
        <div className="upload-section">
          <button
            type="button"
            className="upload-button-blue"
            onClick={() => setShowUploadModal(true)}
          >
            Upload
          </button>
        </div>
      </div>

      <div className="documents-grid">
        {loadingDocuments ? (
          <div
            className="loading-container"
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '40px'
            }}
          >
            <Loader />
          </div>
        ) : (
          documents.map((doc) => (
            <div
              key={doc.id}
              className={`document-card ${
                openDropdownId === doc.id ? 'dropdown-open' : ''
              }`}
            >
              <div className="document-icon-wrapper">
                <svg
                  className="document-icon"
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2Z"
                    stroke="#60bfda"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M14 2V8H20"
                    stroke="#60bfda"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>

              <div className="doc-co">
                <p className="document-title">{doc.name}</p>
                <p className="document-date">{doc.date}</p>

                <div className="document-dropdown-container">
                  <button
                    className="document-menu-btn"
                    onClick={(e) => handleToggleDropdown(doc.id, e)}
                    title="More options"
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <circle cx="12" cy="5" r="1.5" fill="currentColor" />
                      <circle cx="12" cy="12" r="1.5" fill="currentColor" />
                      <circle cx="12" cy="19" r="1.5" fill="currentColor" />
                    </svg>
                  </button>

                  {openDropdownId === doc.id && (
                    <div className="document-dropdown-menu">

                      <div
                        className="document-dropdown-item"
                        onClick={(e) =>
                          handleDeleteFromDropdown(doc.id, e)
                        }
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <span>Delete</span>
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M9 18l6-6-6-6"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>

                      <div
                        className="document-dropdown-item"
                        onClick={(e) =>
                          handleDownloadFromDropdown(doc.id, e)
                        }
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M7 10l5 5 5-5"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M12 15V3"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <span>Download</span>
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M9 18l6-6-6-6"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>

                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  </div>

  {/* ================= NOTES TAB ================= */}
  <div
    className="tab-content"
    style={{ display: activeTab === 'notes' ? 'block' : 'none' }}
  >
    <div className="notes-content">
      <h3>Notes & Comments</h3>
    </div>
  </div>

  {/* ================= REMOTE TAB ================= */}
  {isAdmin && (
    <div
      className="tab-content"
      style={{ display: activeTab === 'remote' ? 'block' : 'none' }}
    >
      <RemoteAccess 
        key={`remote-${remoteKey}`}
        selectedAssetRecordId={selectedAssetRecordId}
      />
    </div>
  )}
</div>

      </div>
      {/* Upload Document Modal */}
      {showUploadModal && (
        <div className="upload-modal-overlay" onClick={handleCloseModal}>
          <div className="upload-modal-content" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="upload-modal-header">
              <h3 className="upload-modal-title">Upload document</h3>
              <button
                className="upload-modal-close-btn"
                onClick={handleCloseModal}
                type="button"
                aria-label="Close"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="upload-modal-body">
              {/* Document Title Input */}
              <div className="upload-modal-input-group">
                <label className="upload-modal-label">Document title</label>
                <input
                  type="text"
                  className="upload-modal-input"
                  placeholder="Enter Document Title"
                  value={documentTitle}
                  onChange={(e) => setDocumentTitle(e.target.value)}
                  style={{
                  cursor: 'not-allowed',
                  opacity: 0.6 // Optional: to indicate it's disabled
                }}
                />
              </div>

              {/* File Upload Area */}
              <div className="upload-modal-file-area">
                <div
                  className={`upload-modal-dropzone ${isDragging ? 'dragging' : ''} ${selectedFile ? 'has-file' : ''}`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => document.getElementById('file-upload-input-modal')?.click()}
                >
                  <input
                    type="file"
                    id="file-upload-input-modal"
                    className="upload-modal-file-input"
                    onChange={handleFileSelect}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png"
                    style={{ display: 'none' }}
                  />

                  <div className="upload-modal-dropzone-content">
                    <div className="upload-modal-file-icon">
                      <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                        <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <div className="red-x-overlay">×</div>
                    </div>
                    {selectedFile ? (
                      <p className="upload-modal-file-name">{selectedFile.name}</p>
                    ) : (
                      <>
                        <p className="upload-modal-file-text">Choose File To Upload</p>
                        <p className="upload-modal-file-hint">Or Drag and Drop Them Here</p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="upload-modal-footer">
              <button
                type="button"
                className="upload-modal-save-button"
                onClick={SaveDocument}
              >
                Save
              </button>
              <button
                type="button"
                className="upload-modal-close-button"
                onClick={handleCloseModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="delete-confirm-modal-overlay" onClick={handleCloseDeleteModal}>
          <div className="delete-confirm-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="delete-confirm-modal-body">
              <p className="delete-confirm-message">Are you sure you want to delete this document?</p>
            </div>
            <div className="delete-confirm-modal-footer">
              <button
                type="button"
                className="delete-confirm-cancel-btn"
                onClick={handleCloseDeleteModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="delete-confirm-ok-btn"
                onClick={handleConfirmDelete}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Overwrite Confirmation Modal */}
      {showOverwriteModal && (
        <div className="delete-confirm-modal-overlay" onClick={handleCancelOverwrite}>
          <div className="delete-confirm-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="delete-confirm-modal-body">
              <p className="delete-confirm-message">
                A document with the name "{duplicateDocumentInfo?.name}" already exists. Do you want to overwrite it?
              </p>
            </div>
            <div className="delete-confirm-modal-footer">
              <button
                type="button"
                className="delete-confirm-cancel-btn"
                onClick={handleCancelOverwrite}
              >
                No
              </button>
              <button
                type="button"
                className="delete-confirm-ok-btn"
                onClick={handleConfirmOverwrite}
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Container */}
      <ToastContainer
        className="toastify-asset-summary"
        position="top-right"
        autoClose={2000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        style={{ top: '80px', right: '20px', zIndex: 10002 }}
      />
    </div>
  );
};

// Info Widget Component
const InfoWidget: React.FC<{ assetDetails: AssetDetails | null; loading: boolean }> = ({ assetDetails, loading }) => {
  if (loading) {
    return (
      <div className="widget info-widget">
        <div className="widget-header">
          <h3>Info</h3>
        </div>
        <div className='loading-container'>
          <Loader/>
        </div>
      </div>
    );
  }

  return (
    <div className="widget info-widget">
      <div className="widget-header">
        <h3>Info</h3>
      </div>
      <div className="widget-content-info">
        {assetDetails ? (
          <div className="info-grid">
            <div className="info-item">
              <span className="label">Plant Name:</span>
              <span className="value">{assetDetails.plant || 'N/A'}</span>
            </div>
            <div className="info-item">
              <span className="label">Asset Type:</span>
              <span className="value">{assetDetails.assetType || 'N/A'}</span>
            </div>
            <div className="info-item">
              <span className="label">Asset Unique ID/Name:</span>
              <span className="value">{assetDetails.assetName || 'N/A'}</span>
            </div>
            <div className="info-item">
              <span className="label">Status:</span>
              <span className={`value ${assetDetails.status?.toLowerCase() === 'active' ? 'status-active' : ''}`}>
                {assetDetails.status || 'N/A'}
              </span>
            </div>
            <div className="info-item">
              <span className="label">Gateway Status:</span>
              <span className={`value ${assetDetails.ioTDeviceConnectionState?.toLowerCase() === 'active' ? 'status-active' : ''}`}>
                {assetDetails.ioTDeviceConnectionState || 'N/A'}
              </span>
            </div>
            <div className="info-item">
              <span className="label">Gateway#:</span>
              <span className="value">{assetDetails.gatewayDeviceId || 'N/A'}</span>
            </div>
            <div className="info-item">
              <span className="label">Controller#:</span>
              <span className="value">{assetDetails.controllerDeviceId || 'N/A'}</span>
            </div>
            <div className="info-item">
              <span className="label">Last Data Received:</span>
              <span className="value">
                {formatDateTime(assetDetails.lastCommunication)}
              </span>
            </div>
            <div className="info-item">
              <span className="label">Commissioned on :</span>
              <span className="value">
                {formatDateTime(assetDetails.firstCommunication)}
              </span>
            </div>
          </div>
        ) : (
          <div className="info-item">
            <span className="label">No info available</span>
          </div>
        )}
      </div>
    </div>
  );
};

// Shared helper function to dynamically determine if a sensor is relay-related
const isRelaySensor = (attribute: any): boolean => {
  if (!attribute) return false;
  
  // Get both sensor name fields for checking
  const sensorName = (attribute.sensorName || '').toLowerCase();
  const displayName = (attribute.name || '').toLowerCase();
  
  // First, exclude known measurement sensors explicitly by checking both name fields
  const measurementSensorNames = [
    'ph', 'temperature', 'conductivity', 'orp', 'dissolved oxygen', 'turbidity',
    'fluorescein', 'sulfite', 'chlorine', 'pressure', 'flow rate', 'level',
    'user custom', 'custom', 'water meter'
  ];
  
  const isMeasurementSensor = measurementSensorNames.some(name => 
    sensorName.includes(name) || displayName.includes(name) || 
    displayName.startsWith(name) || sensorName.startsWith(name)
  );
  
  if (isMeasurementSensor) return false;
  
  // Primary criteria: Check for explicit relay-related naming patterns
  const relayKeywords = [
    'relay', 'pump', 'valve', 'switch', 'motor', 'heater', 'cooler',
    'blower', 'feeder', 'dosing', 'chemical', 'alarm', 'led', 'light',
    'solenoid', 'modem', 'power pack'
  ];
  
  const hasRelayNaming = relayKeywords.some(keyword => 
    sensorName.includes(keyword) || displayName.includes(keyword)
  );
  
  // For explicit relay naming, return true immediately
  if (hasRelayNaming) return true;
  
  // Secondary criteria: Must have position starting with 'R' (Relay position) or 'D' (Digital position)
  const position = (attribute.position || '').toUpperCase();
  const isRelayPosition = position.startsWith('R') || position.startsWith('D');
  
  // Check for binary control characteristics
  const hasBinaryValue = (attribute.value === 0 || attribute.value === 1) && !isNaN(Number(attribute.value));
  const hasControlSetpoint = attribute.setpoint !== null && attribute.setpoint !== undefined && attribute.setpoint !== 'N/A';
  
  // Must have relay position AND binary characteristics for sensors without explicit relay names
  return isRelayPosition && hasBinaryValue;
};

const AssetPropertiesWidget: React.FC<{
  assetDetails: AssetDetails | null;
  loading: boolean;
  formatAttributeValue: (value: number | null | undefined) => string;
  lastCommunication?: string | null;
}> = ({ assetDetails, loading, formatAttributeValue, lastCommunication }) => {

  // Helper function to check if sensor contains "imbalance" keyword
  const hasImbalanceKeyword = (attribute: AssetAttribute): boolean => {
    return attribute.name.toLowerCase().includes('imbalance');
  };

  // Filter out relay-related sensors and imbalance sensors from the properties display
  const nonRelayAttributes = assetDetails?.attributes?.filter(attr => !isRelaySensor(attr) && !hasImbalanceKeyword(attr)) || [];

  // Helper function to clean sensor names by removing "(Imbalance)"
  const cleanSensorName = (name: string): string => {
    return name.replace(/\s*\(Imbalance\)$/i, '');
  };

  return (
    <div className="widget asset-properties-widget">

      {/* TITLE */}
      <div className="widget-header">
        <h3>
          Properties
          {lastCommunication ? ` (${formatDateTime(lastCommunication)})` : ''}
        </h3>
      </div>

      
      <div className="properties-table-header">
        <div className="col sensor">Sensor</div>
        <div className="col reading">Last Reading</div>
        <div className="col alarm-l">Alarm L</div>
        <div className="col alarm-h">Alarm H</div>
      </div>

      {/* CONTENT */}
      <div className="widget-content">

        {loading ? (
          <div className="loading-container">
            <Loader />
          </div>
        ) : nonRelayAttributes.length ? (

          nonRelayAttributes.map((attribute, index) => (
            <div key={index} className="properties-row">
              <div className="label">{cleanSensorName(attribute.name)}</div>

              <div className="value">
                {formatAttributeValue(attribute.value)}
                {attribute.value != null && attribute.unit ? `\u00A0${attribute.unit}` : ''}
              </div>

              <div className="value">{attribute.thresholdL ?? '—'}</div>
              <div className="value">{attribute.thresholdH ?? '—'}</div>
            </div>
          ))

        ) : (
          <div className="properties-row">
            <div>No attributes available</div>
            <div></div>
            <div></div>
            <div></div>
          </div>
        )}

      </div>
    </div>
  );
};


// Sensor Status Widget Component
const SensorStatusWidget: React.FC<{ assetDetails: AssetDetails | null; loading: boolean }> = ({ assetDetails, loading }) => {
  if (loading) {
    return (
      <div className="widget sensor-status-widget">
        <div className="widget-header">
          <h3>Relay Status</h3>
        </div>
        
        <div className='loading-container'>
          <Loader/>
        </div>
      </div>
    );
  }

  // Helper function to check if sensor contains "imbalance" keyword
  const hasImbalanceKeyword = (attribute: AssetAttribute): boolean => {
    return attribute.name.toLowerCase().includes('imbalance');
  };

  // Filter attributes to find relay-related items using shared function, excluding imbalance sensors
  const relayAttributes = assetDetails?.attributes?.filter(attr => isRelaySensor(attr) && !hasImbalanceKeyword(attr)) || [];

  return (
  <div className="widget sensor-status-widget">

    {/* TITLE */}
    <div className="widget-header">
      <h3>Relay Status</h3>
    </div>

    
    <div className="relay-table-header">
      <div className="col relay">Relay Label</div>
      <div className="col mode">Mode</div>
      <div className="col point">Set Point</div>
    </div>

    {/* CONTENT */}
    <div className="widget-content">

      {relayAttributes.length > 0 ? (
        relayAttributes.map((relay, index) => (
          <div key={index} className="relay-row">

            <div className="label">{relay.name}</div>

            <div className="value">
  <span
    className={`relay-status ${relay.value === 1 ? 'on' : 'off'}`}
  >
    {relay.value === 1 ? 'ON' : 'OFF'}
  </span>
</div>


            <div className="value">
              {relay.setpoint ?? '—'}
            </div>

          </div>
        ))
      ) : (
        <div className="relay-row">
          <div className="label">
            {assetDetails ? 'No relay information available' : 'No asset selected'}
          </div>
          <div></div>
          <div></div>
        </div>
      )}

    </div>
  </div>
);

};

// Optional: Recent Alerts Widget using API data
const RecentAlertsWidget: React.FC<{ assetDetails: AssetDetails | null; loading: boolean }> = ({ assetDetails, loading }) => {
  if (loading) {
    return (
      <div className="widget recent-alerts-widget">
        <div className="widget-header">
          <h3>Recent Alerts</h3>
        </div>
        <div className="widget-content">
          <div className="loading-spinner">Loading...</div>
        </div>
      </div>
    );
  }

  const formatAlertTime = (alertTime: string): string => {
    try {
      const date = new Date(alertTime);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return alertTime;
    }
  };

  const getSeverityClass = (severity: string): string => {
    const severityLower = severity?.toLowerCase() || '';
    switch (severityLower) {
      case 'critical':
      case 'high':
        return 'severity-critical';
      case 'warning':
      case 'medium':
        return 'severity-warning';
      case 'info':
      case 'low':
        return 'severity-info';
      default:
        return 'severity-info';
    }
  };

  return (
    <div className="widget recent-alerts-widget">
      <div className="widget-header">
        <h3>Recent Alerts</h3>
      </div>
      <div className="widget-content">
        {assetDetails?.alerts && assetDetails.alerts.length > 0 ? (
          <div className="alerts-list">
            {assetDetails.alerts.slice(0, 5).map((alert, index) => (
              <div key={index} className="alert-item">
                <div className="alert-info">
                  <span className="alert-reason">{alert.reason || 'Unknown'}</span>
                  <span className="alert-time">{formatAlertTime(alert.alertTime)}</span>
                </div>
                <span className={`alert-severity ${getSeverityClass(alert.severity)}`}>
                  {alert.severity || 'Unknown'}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="no-alerts">
            <span>No recent alerts</span>
          </div>
        )}
      </div>
    </div>
  );
};

// Alerts Widget Component
interface AlertsWidgetProps {
  assetId?: string;
  assetDetails?: AssetDetails | null;
  loading?: boolean;
}

const AlertsWidget: React.FC<AlertsWidgetProps> = ({ assetId, assetDetails, loading }) => {
  const [selectedTimeRange, setSelectedTimeRange] = useState<string>('24 hours');
  const [showCalendarModal, setShowCalendarModal] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [selectingStart, setSelectingStart] = useState<boolean>(true);
  const [filteredAlerts, setFilteredAlerts] = useState<AssetAlert[]>([]);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [apiAlerts, setApiAlerts] = useState<AssetAlert[]>([]);
  const [loadingAlerts, setLoadingAlerts] = useState<boolean>(false);

  const calendarModalRef = useRef<HTMLDivElement>(null);
  const lastApiCallRef = useRef<string>(''); // Track last API call to prevent duplicates

  const timeRangeOptions = [
    '24 hours',
    '7 days',
    'Select Date Range'
  ];

  // Function to format date for API (YYYY-MM-DD format)
  const formatDateForAPI = (date: Date): string => {
    return date.toISOString().split('T')[0];
  };

  // Function to format date for display (DD/MM/YYYY format)
  const formatDateForDisplay = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Function to get severity class for styling
  const getSeverityClass = (severity: string): string => {
    if (!severity) return 'unknown';
    const severityLower = severity.toLowerCase();
    switch (severityLower) {
      case 'critical':
      case 'high':
      case 'danger':
        return 'critical';
      case 'medium':
      case 'warning':
        return 'warning';
      case 'low':
      case 'info':
        return 'info';
      case 'failed':
      case 'error':
        return 'failed';
      default:
        return 'unknown';
    }
  };

  // Calendar modal handlers (following AnalysisTab pattern)
  const handleCalendarToggle = () => {
    setShowCalendarModal(!showCalendarModal);
  };

  const handleTimeRangeSelect = (range: string) => {
    setSelectedTimeRange(range);
    if (range !== 'Select Date Range') {
      setShowCalendarModal(false);
      // Filter alerts for predefined ranges
      filterAlerts(range);
    } else {
      // Open calendar modal for custom date range
      setShowCalendarModal(true);
    }
  };

  const handleDateRangeApply = () => {
    if (startDate && endDate) {
      setShowCalendarModal(false);
      setSelectedTimeRange('Select Date Range');
    }
  };

  const handleDateClick = (date: Date) => {
    if (selectingStart || !startDate) {
      setStartDate(date);
      setSelectingStart(false);
    } else {
      if (date < startDate) {
        setStartDate(date);
        setEndDate(startDate);
      } else {
        setEndDate(date);
      }
      setSelectingStart(true);
    }
  };

  const isDateInRange = (date: Date): boolean => {
    if (!startDate || !endDate) return false;
    return date >= startDate && date <= endDate;
  };

  const isStartDate = (date: Date): boolean => {
    return startDate ? date.toDateString() === startDate.toDateString() : false;
  };

  const isEndDate = (date: Date): boolean => {
    return endDate ? date.toDateString() === endDate.toDateString() : false;
  };

  const getDaysInMonth = (date: Date): Date[] => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days: Date[] = [];
    for (let i = 0; i < 42; i++) {
      days.push(new Date(startDate));
      startDate.setDate(startDate.getDate() + 1);
    }
    return days;
  };

  const navigateMonth = (direction: number) => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + direction, 1));
  };

  const resetDateSelection = () => {
    setStartDate(null);
    setEndDate(null);
    setSelectingStart(true);
  };

// In the fetchAlertsFromAPI function (around line 2040)
const fetchAlertsFromAPI = async (startDateStr: string, endDateStr: string) => {
  if (!assetId) {
    return;
  }

  // Create a unique key for this API call to prevent duplicates
  const apiCallKey = `${assetId}-${startDateStr}-${endDateStr}`;
  if (lastApiCallRef.current === apiCallKey) {
    return;
  }

  lastApiCallRef.current = apiCallKey;
  setLoadingAlerts(true);
  
  try {
    // Get user's timezone from preferences or use browser timezone
    const savedPreferences = localStorage.getItem('userPreferences');
    let userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    if (savedPreferences) {
      try {
        const prefs = JSON.parse(savedPreferences);
        if (prefs.timeZone) {
          userTimezone = prefs.timeZone;
        }
      } catch (e) {
      }
    }
       const getRelativeTimerange = (): number => {
      switch (selectedTimeRange) {
        case '24 hours':
          return 4;
        case '7 days':
          return 5;
        default:
          return 6; // Custom date range
      }
    };
    const timeRange= getRelativeTimerange();

    // Call getAssetAlerts with timezone parameter
    const alerts = await getAssetAlerts(assetId, startDateStr, endDateStr,timeRange);
    setApiAlerts(alerts);
    setFilteredAlerts(alerts);
  } catch (error) {
    setApiAlerts([]);
    setFilteredAlerts([]);
  } finally {
    setLoadingAlerts(false);
  }
};

  // Function to filter alerts based on time range
  const filterAlerts = (range: string) => {
    const now = new Date();
    let startTime: Date;
    let endTime: Date = now;

    switch (range) {
      case '24 hours':
        startTime = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        break;
      case '7 days':
        startTime = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      default:
        // Default to 24 hours for any other case
        startTime = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        break;
    }

    // Format dates for API call (YYYY-MM-DD)
    const startDateStr = startTime.toISOString().split('T')[0];
    const endDateStr = endTime.toISOString().split('T')[0];

    // Fetch alerts from API for the selected range
    fetchAlertsFromAPI(startDateStr, endDateStr);
  };

  // Filter alerts when assetDetails or time range changes
  useEffect(() => {
    // Reset API call tracker when assetId changes
    lastApiCallRef.current = '';

    if (assetId && selectedTimeRange !== 'Select Date Range') {
      // Only trigger for predefined ranges, not custom date ranges
      filterAlerts(selectedTimeRange);
    } else if (assetDetails?.alerts) {
      // Fallback to alerts from assetDetails if no assetId
      setFilteredAlerts(assetDetails.alerts);
    }
  }, [assetDetails, selectedTimeRange, assetId]);

  // Handle custom date range separately to avoid duplicate calls
  useEffect(() => {
    if (selectedTimeRange === 'Select Date Range' && startDate && endDate && assetId) {
      const startDateStr = startDate.toISOString().split('T')[0];
      const endDateStr = endDate.toISOString().split('T')[0];
      fetchAlertsFromAPI(startDateStr, endDateStr);
    }
  }, [startDate, endDate, assetId]);

   useEffect(() => {
  return () => {
    // ✅ SAFETY CLEANUP when component unmounts
    document.body.classList.remove('widget-expanded-body');
  };
}, []);

useLayoutEffect(() => {
  const handleEscKey = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && isExpanded) {
      setIsExpanded(false);
      document.body.classList.remove('widget-expanded-body');
    }
  };

  if (isExpanded) {
    document.body.classList.add('widget-expanded-body');
    document.addEventListener('keydown', handleEscKey);
  }

  return () => {
    document.body.classList.remove('widget-expanded-body');
    document.removeEventListener('keydown', handleEscKey);
  };
}, [isExpanded]);



const handleExpandClick = () => {
  setIsExpanded(prev => {
    const next = !prev;

    if (next) {
      document.body.classList.add('widget-expanded-body');
    } else {
      document.body.classList.remove('widget-expanded-body');
    }

    return next;
  });
};
  // Close calendar modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        showCalendarModal &&
        calendarModalRef.current &&
        !calendarModalRef.current.contains(event.target as Node)
      ) {
        setShowCalendarModal(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCalendarModal]);

  return (
    <div className={`widget alerts-widget ${isExpanded ? 'expanded' : ''}`}>
         {isExpanded && (
          <button
            className='alerts-close'
            onClick={() => setIsExpanded(false)}
            title="Close"
            aria-label="Close fullscreen"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path
                d="M18 6L6 18M6 6l12 12"
                stroke="red"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
    <div className="widget-header">
        <div className="widget-title-row">
          <h3>Alerts <span className="alerts-badge">({filteredAlerts.length})</span></h3>
          <div className="widget-actions">
            <button
          className="widget-action-btn expand-btn"
          title={isExpanded ? 'Close' : 'Expand'}
          onClick={() => setIsExpanded(prev => !prev)}
          >
          
          {isExpanded ? (
            
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            ) : (
              // ⛶ Expand icon
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3
                        m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </button>

          </div>
        </div>
        <div className="widget-controls-row">
          <div className="alerts-filter-buttons" style={{ position: 'relative' }}>
            {timeRangeOptions.map((option) => (
              <button
                key={option}
                className={`alert-filter-btn ${selectedTimeRange === option || (option === 'Select Date Range' && selectedTimeRange === 'Select Date Range' && startDate && endDate) ? 'active' : ''}`}
                onClick={() => handleTimeRangeSelect(option)}
                style={{
                  padding: '8px 16px',
                  margin: '0 4px',
                  background: selectedTimeRange === option || (option === 'Select Date Range' && selectedTimeRange === 'Select Date Range' && startDate && endDate) ? '#4a5463' : 'transparent',
                  color: '#e0e0e0',
                  border: '1px solid #4a5463',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                {option === 'Select Date Range' ? (
                  <>
                    {selectedTimeRange === 'Select Date Range' && startDate && endDate
                      ? `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
                      : option}
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      style={{ marginLeft: '4px' }}
                    >
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" stroke="currentColor" strokeWidth="2" />
                      <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="2" />
                      <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="2" />
                      <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="2" />
                    </svg>
                  </>
                ) : (
                  option
                )}
              </button>
            ))}

            {/* Calendar Modal - only shown when Select date range is clicked */}
            {showCalendarModal && (
              <div
                className='calendar-modal-container'
                ref={calendarModalRef}
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: '0',
                  right: 'auto',
                  background: '#22314a',
                  border: '1px solid #4a5463',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                  zIndex: 9999,
                  minWidth: '350px',
                  marginTop: '8px'
                }}
              >
                <div style={{ padding: '16px' }}>
                  <div className="calendar-section">
                    <div className="calendar-header" style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '12px'
                    }}>
                      <button
                        className="calendar-nav-btn"
                        onClick={() => navigateMonth(-1)}
                        style={{
                          background: 'transparent',
                          border: '1px solid #4a5463',
                          color: '#e0e0e0',
                          borderRadius: '4px',
                          width: '32px',
                          height: '32px',
                          cursor: 'pointer'
                        }}
                      >‹</button>
                      <span style={{ color: '#e0e0e0', fontSize: '14px', fontWeight: 'bold' }}>
                        {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                      </span>
                      <button
                        className="calendar-nav-btn"
                        onClick={() => navigateMonth(1)}
                        style={{
                          background: 'transparent',
                          border: '1px solid #4a5463',
                          color: '#e0e0e0',
                          borderRadius: '4px',
                          width: '32px',
                          height: '32px',
                          cursor: 'pointer'
                        }}
                      >›</button>
                    </div>

                    <div className="calendar-days-header" style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(7, 1fr)',
                      gap: '4px',
                      marginBottom: '8px'
                    }}>
                      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                        <div key={day} style={{
                          padding: '8px 4px',
                          textAlign: 'center',
                          fontSize: '12px',
                          color: '#bfc9db',
                          fontWeight: 'bold'
                        }}>
                          {day}
                        </div>
                      ))}
                    </div>

                    <div className="calendar-days-grid" style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(7, 1fr)',
                      gap: '4px',
                      marginBottom: '12px'
                    }}>
                      {getDaysInMonth(currentMonth).map((date, index) => {
                        const isCurrentMonth = date.getMonth() === currentMonth.getMonth();
                        const isToday = date.toDateString() === new Date().toDateString();
                        const isStart = isStartDate(date);
                        const isEnd = isEndDate(date);
                        const isInRange = isDateInRange(date);

                        let dayStyle: React.CSSProperties = {
                          padding: '8px 4px',
                          textAlign: 'center',
                          fontSize: '12px',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: isCurrentMonth ? 'pointer' : 'default',
                          background: 'transparent',
                          color: isCurrentMonth ? '#e0e0e0' : '#666',
                        };

                        if (isToday && isCurrentMonth) {
                          dayStyle.background = 'rgba(74, 84, 99, 0.5)';
                          dayStyle.color = '#fff';
                        }

                        if (isStart || isEnd) {
                          dayStyle.background = '#4a5463';
                          dayStyle.color = '#fff';
                        } else if (isInRange && isCurrentMonth) {
                          dayStyle.background = 'rgba(74, 84, 99, 0.3)';
                          dayStyle.color = '#e0e0e0';
                        }

                        return (
                          <button
                            key={index}
                            style={dayStyle}
                            onClick={() => isCurrentMonth && handleDateClick(date)}
                            disabled={!isCurrentMonth}
                          >
                            {date.getDate()}
                          </button>
                        );
                      })}
                    </div>

                    {(startDate || endDate) && (
                      <div className="date-selection-info" style={{
                        marginTop: '12px',
                        padding: '8px',
                        background: 'rgba(74, 84, 99, 0.2)',
                        borderRadius: '4px'
                      }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: '12px',
                          color: '#bfc9db'
                        }}>
                          <span>Start: {startDate ? startDate.toLocaleDateString() : 'Not selected'}</span>
                          <span>End: {endDate ? endDate.toLocaleDateString() : 'Not selected'}</span>
                        </div>
                      </div>
                    )}

                    <div className="calendar-footer" style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: '8px',
                      marginTop: '16px'
                    }}>
                      <button
                        className="reset-btn"
                        onClick={resetDateSelection}
                        style={{
                          background: 'transparent',
                          border: '1px solid #4a5463',
                          color: '#e0e0e0',
                          borderRadius: '4px',
                          padding: '8px 16px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        Reset
                      </button>
                      <button
                        className={`apply-btn ${(!startDate || !endDate) ? 'disabled' : ''}`}
                        onClick={handleDateRangeApply}
                        disabled={!startDate || !endDate}
                        style={{
                          background: (!startDate || !endDate) ? 'rgba(74, 84, 99, 0.5)' : '#4a5463',
                          border: '1px solid #4a5463',
                          color: (!startDate || !endDate) ? 'rgba(224, 224, 224, 0.5)' : '#e0e0e0',
                          borderRadius: '4px',
                          padding: '8px 16px',
                          cursor: (!startDate || !endDate) ? 'not-allowed' : 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        Apply Custom Range
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="widget-content">
        {loadingAlerts ? (
          <div className="loading-state">Loading alerts...</div>
        ) : filteredAlerts.length === 0 ? (
          <div className="no-alerts-state">No alerts found for the selected time range.</div>
        ) : (
          <div className="alerts-table">
            {filteredAlerts.map((alert: AssetAlert, index: number) => (
              <div key={index} className="alert-row">
                <div className="alert-date-col">{formatDateForDisplay(alert.alertTime)}</div>
                <div className="alert-message-col">
                  <div className="alert-reason">{alert.reason}</div>
                  <div className="alert-attribute">
                    {alert.reason === "Device Communication Lost" 
                      ? alert.attribute 
                      : `${alert.attribute}: ${alert.value}`}
                  </div>
                </div>
                <div className="alert-status-col">
                  <span className={`alert-status ${getSeverityClass(alert.severity)}`}>
                    {alert.severity}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Connectivity Trend Widget Component
interface ConnectivityTrendWidgetProps {
  assetId: string;
  assetDetails: AssetDetails | null;
}

const ConnectivityTrendWidget: React.FC<ConnectivityTrendWidgetProps> = ({ assetId, assetDetails }) => {
  const chartDiv = useRef<HTMLDivElement>(null);
  const [selectedTimeRange, setSelectedTimeRange] = useState<string>('7 days');
  const [showCalendarModal, setShowCalendarModal] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [selectingStart, setSelectingStart] = useState<boolean>(true);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [connectivityData, setConnectivityData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const calendarModalRef = useRef<HTMLDivElement>(null);

  const timeRangeOptions = [
    '7 days',
    'Select Date Range'
  ];

  // Function to calculate date range based on selected option
  const getDateRange = (timeRange: string): { startTime: string, endTime: string } => {
    const now = new Date();
    const endTime = now.toISOString();
    let startTime: string;

    switch (timeRange) {
      case '7 days':
        startTime = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
        break;
      default:
        if (startDate && endDate) {
          startTime = startDate.toISOString();
          return { startTime, endTime: endDate.toISOString() };
        }
        // Default to 24 hours
        startTime = new Date(now.getTime() -7 * 24 * 60 * 60 * 1000).toISOString();
    }

    return { startTime, endTime };
  };

  // Function to fetch connectivity data from API
const fetchConnectivityData = async (timeRange: string) => {
  if (!assetId) return;

  setLoading(true);
  try {
    const { startTime, endTime } = getDateRange(timeRange);


    // Get user's timezone from preferences or fallback to browser timezone
    let userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    // Try to get user's preferred timezone from localStorage
    const savedPreferences = localStorage.getItem('userPreferences');
    if (savedPreferences) {
      try {
        const prefs = JSON.parse(savedPreferences);
        if (prefs.timeZone) {
          userTimeZone = prefs.timeZone;
        }
      } catch (e) {
      }
    }

          const getRelativeTimerange = (): number => {
      switch (selectedTimeRange) {
        case '7 days':
          return 5;
        default:
          return 6; // Custom date range
      }
    };
    const timeRangeValue = getRelativeTimerange();

    const request: StatusQueryRequest = {
      assetId: assetId, // Dynamic asset ID from props
      channelIds: ['M13'], // Static channel ID as requested
      startTime,
      endTime,
      timeRange: timeRangeValue,
    };
    // ✅ Custom date range → send 6 + dates
    if (timeRangeValue === 6) {
      const { startTime, endTime } = getDateRange(selectedTimeRange);
      request.startTime = startTime;
      request.endTime = endTime;
    }


    const response = await postStatusQuery(request);

    // Transform API response to chart data format (use value as percent)
    let transformedData: any[] = [];
    const responseData = response as any;
    if (responseData && responseData.data && responseData.data.Data && responseData.data.Data.length > 0) {
      const channelData = responseData.data.Data[0];
      if (channelData.data && channelData.data.length > 0) {
        transformedData = channelData.data.map((item: any) => {
          const dateStr = new Date(item.timestamp).toLocaleDateString('en-GB', { 
            day: '2-digit', 
            month: 'short' 
          });
          const communicationPercent = Math.round(item.value);
          return {
            date: dateStr,
            communicationPercent,
            nonCommunicationPercent: 100 - communicationPercent
          };
        });
      }
    }
    setConnectivityData(transformedData);
  } catch (error: unknown) {

    // Type guard for Axios error
    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as any;
    }

    // No fallback data - set empty array on error
    setConnectivityData([]);
  } finally {
    setLoading(false);
  }
};
  // Effect to fetch data when component mounts or timeRange/assetId changes
  useEffect(() => {
    if (assetDetails) { // Only fetch when assetDetails is available
      fetchConnectivityData(selectedTimeRange);
    }
  }, [selectedTimeRange, assetId, assetDetails]);

  // Handle time range change
  const handleTimeRangeSelect = (range: string) => {
    setSelectedTimeRange(range);
    if (range !== 'Select Date Range') {
      setShowCalendarModal(false);
      // Only update state, let useEffect handle fetch
    } else {
      // Open calendar modal for custom date range
      setShowCalendarModal(true);
    }
  };

  const handleCalendarToggle = () => {
    setShowCalendarModal(!showCalendarModal);
  };

  const handleDateRangeApply = () => {
    if (startDate && endDate) {
      setShowCalendarModal(false);
      setSelectedTimeRange('Select Date Range');
      // Fetch data for custom range
      fetchConnectivityData('custom');
    } else {
    }
  };

  const handleDateClick = (date: Date) => {
    if (selectingStart || !startDate) {
      setStartDate(date);
      setSelectingStart(false);
    } else {
      if (date < startDate) {
        setStartDate(date);
        setEndDate(startDate);
      } else {
        setEndDate(date);
      }
      setSelectingStart(true);
    }
  };

  const isDateInRange = (date: Date): boolean => {
    if (!startDate || !endDate) return false;
    return date >= startDate && date <= endDate;
  };

  const isStartDate = (date: Date): boolean => {
    return startDate ? date.toDateString() === startDate.toDateString() : false;
  };

  const isEndDate = (date: Date): boolean => {
    return endDate ? date.toDateString() === endDate.toDateString() : false;
  };

  const getDaysInMonth = (date: Date): Date[] => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days: Date[] = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(new Date(year, month, 1 - startingDayOfWeek + i));
    }

    // Add all days of the current month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    // Add days from next month to fill the calendar grid
    const remainingCells = 42 - days.length; // 6 rows × 7 days = 42 cells
    for (let day = 1; day <= remainingCells; day++) {
      days.push(new Date(year, month + 1, day));
    }

    return days;
  };

  const navigateMonth = (direction: number) => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + direction, 1));
  };

  const resetDateSelection = () => {
    setStartDate(null);
    setEndDate(null);
    setSelectingStart(true);
  };

  useLayoutEffect(() => {
    if (!chartDiv.current) return;

    // Create root element with license
    const root = createAmChartsRoot(chartDiv.current);

    // Set themes
    root.setThemes([
      am5themes_Dark.new(root)
    ]);

    // Create chart
    const chart = root.container.children.push(am5xy.XYChart.new(root, {
      panX: false,
      panY: false,
      wheelX: "none",
      wheelY: "none",
      pinchZoomX: false,
      paddingLeft: 0,
      paddingRight: 1
    }));

    // Add cursor
    const cursor = chart.set("cursor", am5xy.XYCursor.new(root, {}));
    cursor.lineY.set("visible", false);

    // Create axes
    const xRenderer = am5xy.AxisRendererX.new(root, {
      minGridDistance: 30,
      minorGridEnabled: true
    });
    xRenderer.labels.template.setAll({
      rotation: 0,
      centerY: am5.p100,
      centerX: am5.p50,
      paddingRight: 0
    });
    xRenderer.grid.template.setAll({ location: 1 });
    const xAxis = chart.xAxes.push(am5xy.CategoryAxis.new(root, {
      maxDeviation: 0.3,
      categoryField: "date",
      renderer: xRenderer,
      tooltip: am5.Tooltip.new(root, {})
    }));

    // yAxis.get("renderer").labels.template.setAll({
       const yRenderer = am5xy.AxisRendererY.new(root, {
      strokeOpacity: 0.1
    });

    const yAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, {
      maxDeviation: 0.3,
      renderer: yRenderer,
      numberFormat: "#'%'",
      min: 0,
      max: 100,
      strictMinMax: true
    }));

    // Format y-axis labels to show percentage symbol
    yAxis.get("renderer").labels.template.setAll({
      text: "{value}%"
    });

    // Green series (communication)
    const greenSeries = chart.series.push(am5xy.ColumnSeries.new(root, {
      name: "Communication",
      xAxis: xAxis,
      yAxis: yAxis,
      valueYField: "communicationPercent",
      categoryXField: "date",
      stacked: true,
      fill: am5.color("#3ec46d"),
      stroke: am5.color("#3ec46d"),
      tooltip: am5.Tooltip.new(root, {
        labelText: "{categoryX}: {valueY}% Communication"
      })
    }));

    // Red series (non-communication)
    const redSeries = chart.series.push(am5xy.ColumnSeries.new(root, {
      name: "Non-Communication",
      xAxis: xAxis,
      yAxis: yAxis,
      valueYField: "nonCommunicationPercent",
      categoryXField: "date",
      stacked: true,
      fill: am5.color("#e74c3c"),
      stroke: am5.color("#e74c3c"),
      tooltip: am5.Tooltip.new(root, {
        labelText: "{categoryX}: {valueY}% Non-Communication"
      })
    }));

  // Set fixed pixel width for columns
  greenSeries.columns.template.setAll({ width: 30 });
  redSeries.columns.template.setAll({ width: 30 });

  // Store chart references for updates
  (chartDiv.current as any).chartRoot = root;
  (chartDiv.current as any).chartGreenSeries = greenSeries;
  (chartDiv.current as any).chartRedSeries = redSeries;
  (chartDiv.current as any).chartXAxis = xAxis;

  greenSeries.appear(1000);
  redSeries.appear(1000);
  chart.appear(1000, 100);

    return () => {
      root.dispose();
    };
  }, []);

  // Update chart data when connectivityData changes
  useEffect(() => {
    if (
      chartDiv.current &&
      (chartDiv.current as any).chartGreenSeries &&
      (chartDiv.current as any).chartRedSeries &&
      connectivityData.length > 0
    ) {
      const greenSeries = (chartDiv.current as any).chartGreenSeries;
      const redSeries = (chartDiv.current as any).chartRedSeries;
      const xAxis = (chartDiv.current as any).chartXAxis;

      // Clear existing data
      greenSeries.data.clear();
      redSeries.data.clear();
      xAxis.data.clear();

      // Set new data
      xAxis.data.setAll(connectivityData);
      greenSeries.data.setAll(connectivityData);
      redSeries.data.setAll(connectivityData);
    }
  }, [connectivityData]);

  // Handle chart resize when expanded state changes for connectivity widget
  useLayoutEffect(() => {
    if (chartDiv.current) {
      // Force a resize after expansion state change
      const resizeEvent = new Event('resize');
      window.dispatchEvent(resizeEvent);

      // Additional timeout to ensure DOM has updated
      setTimeout(() => {
        const resizeEvent = new Event('resize');
        window.dispatchEvent(resizeEvent);
      }, 100);
    }
  }, [isExpanded]);

  // Handle ESC key to close expanded widget for connectivity widget
  useLayoutEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (showCalendarModal) {
          setShowCalendarModal(false);
        } else if (isExpanded) {
          setIsExpanded(false);
          document.body.classList.remove('widget-expanded-body');
        }
      }
    };

    if (isExpanded || showCalendarModal) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isExpanded, showCalendarModal]);

  // Handle click outside to close calendar modal
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showCalendarModal && calendarModalRef.current && !calendarModalRef.current.contains(event.target as Node)) {
        setShowCalendarModal(false);
      }
    };

    if (showCalendarModal) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCalendarModal]);

const handleExpandClick = () => {
  setIsExpanded(prev => {
    const next = !prev;

    if (next) {
      document.body.classList.add('widget-expanded-body');
    } else {
      document.body.classList.remove('widget-expanded-body');
    }

    return next;
  });
};


  return (
    <>
      <style>{`
        .connectivity-calendar-modal::-webkit-scrollbar {
          width: 6px;
        }
        .connectivity-calendar-modal::-webkit-scrollbar-track {
          background: #22314a;
          border-radius: 3px;
        }
        .connectivity-calendar-modal::-webkit-scrollbar-thumb {
          background: #4a5463;
          border-radius: 3px;
        }
        .connectivity-calendar-modal::-webkit-scrollbar-thumb:hover {
          background: #5a6373;
        }
      `}</style>
      <div className={`widget connectivity-trend-widget ${isExpanded ? 'expanded' : ''}`}>
        <div className="widget-header conductivity">
          <div className="widget-title-row">
            <h3>Connectivity Trend</h3>
            <div className="widget-actions">
              <button className="widget-action-btn expand-btn" title={isExpanded ? "Close" : "Expand"} onClick={handleExpandClick}>
                {isExpanded ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </button>
            </div>
          </div>
          <div className="widget-controls-row">
            <div className="alerts-filter-buttons" style={{ position: 'relative' }}>
              {timeRangeOptions.map((option) => (
                <button
                  key={option}
                  className={`alert-filter-btn ${selectedTimeRange === option || (option === 'Select Date Range' && selectedTimeRange === 'Select Date Range' && startDate && endDate) ? 'active' : ''}`}
                  onClick={() => handleTimeRangeSelect(option)}
                  style={{
                    padding: '8px 16px',
                    margin: '0 4px',
                    background: selectedTimeRange === option || (option === 'Select Date Range' && selectedTimeRange === 'Select Date Range' && startDate && endDate) ? '#4a5463' : 'transparent',
                    color: '#e0e0e0',
                    border: '1px solid #4a5463',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  {option === 'Select Date Range' ? (
                    <>
                      {selectedTimeRange === 'Select Date Range' && startDate && endDate
                        ? `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
                        : option}
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        style={{ marginLeft: '4px' }}
                      >
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" stroke="currentColor" strokeWidth="2" />
                        <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="2" />
                        <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="2" />
                        <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="2" />
                      </svg>
                    </>
                  ) : (
                    option
                  )}
                </button>
              ))}

              {/* Calendar Modal - only shown when Select date range is clicked */}
              {showCalendarModal && (
                <div
                  className='calendar-modal-container connectivity-calendar-modal'
                  ref={calendarModalRef}
                  style={{
                    position: 'absolute',
                    top: '258%',
                    left: '0',
                    right: 'auto',
                    background: '#22314a',
                    border: '1px solid #4a5463',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                    zIndex: 9999,
                    minWidth: '350px',
                    marginTop: '-49px'
                  }}
                >
                  <div style={{ padding: '16px' }}>
                    <div className="calendar-section">
                      <div className="calendar-header" style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '12px'
                      }}>
                        <button
                          className="calendar-nav-btn"
                          onClick={() => navigateMonth(-1)}
                          style={{
                            background: 'transparent',
                            border: '1px solid #4a5463',
                            color: '#e0e0e0',
                            borderRadius: '4px',
                            width: '32px',
                            height: '32px',
                            cursor: 'pointer'
                          }}
                        >‹</button>
                        <span style={{ color: '#e0e0e0', fontSize: '14px', fontWeight: 'bold' }}>
                          {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                        </span>
                        <button
                          className="calendar-nav-btn"
                          onClick={() => navigateMonth(1)}
                          style={{
                            background: 'transparent',
                            border: '1px solid #4a5463',
                            color: '#e0e0e0',
                            borderRadius: '4px',
                            width: '32px',
                            height: '32px',
                            cursor: 'pointer'
                          }}
                        >›</button>
                      </div>

                      <div className="calendar-days-header" style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(7, 1fr)',
                        gap: '4px',
                        marginBottom: '8px'
                      }}>
                        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                          <div key={day} style={{
                            padding: '8px 4px',
                            textAlign: 'center',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            color: '#bfc9db'
                          }}>{day}</div>
                        ))}
                      </div>

                      <div className="calendar-days-grid" style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(7, 1fr)',
                        gap: '4px',
                        marginBottom: '12px'
                      }}>
                        {getDaysInMonth(currentMonth).map((date, index) => {
                          const isCurrentMonth = date.getMonth() === currentMonth.getMonth();
                          const isToday = date.toDateString() === new Date().toDateString();
                          const isStart = isStartDate(date);
                          const isEnd = isEndDate(date);
                          const isInRange = isDateInRange(date);

                          let dayStyle = {
                            padding: '8px 4px',
                            textAlign: 'center' as const,
                            fontSize: '12px',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: isCurrentMonth ? 'pointer' : 'default',
                            background: 'transparent',
                            color: isCurrentMonth ? '#e0e0e0' : '#666',
                          };

                          if (isToday && isCurrentMonth) {
                            dayStyle.background = 'rgba(74, 84, 99, 0.5)';
                            dayStyle.color = '#fff';
                          }

                          if (isStart || isEnd) {
                            dayStyle.background = '#4a5463';
                            dayStyle.color = '#fff';
                          } else if (isInRange && isCurrentMonth) {
                            dayStyle.background = 'rgba(74, 84, 99, 0.3)';
                            dayStyle.color = '#e0e0e0';
                          }

                          return (
                            <button
                              key={index}
                              style={dayStyle}
                              onClick={() => isCurrentMonth && handleDateClick(date)}
                              disabled={!isCurrentMonth}
                            >
                              {date.getDate()}
                            </button>
                          );
                        })}
                      </div>

                      {(startDate || endDate) && (
                        <div className="date-selection-info" style={{
                          marginTop: '12px',
                          padding: '8px',
                          background: 'rgba(74, 84, 99, 0.2)',
                          borderRadius: '4px'
                        }}>
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            fontSize: '12px',
                            color: '#bfc9db'
                          }}>
                            <span>Start: {startDate ? startDate.toLocaleDateString() : 'Not selected'}</span>
                            <span>End: {endDate ? endDate.toLocaleDateString() : 'Not selected'}</span>
                          </div>
                        </div>
                      )}

                      <div className="calendar-footer" style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: '8px',
                        marginTop: '16px'
                      }}>
                        <button
                          className="reset-btn"
                          onClick={resetDateSelection}
                          style={{
                            background: 'transparent',
                            border: '1px solid #4a5463',
                            color: '#e0e0e0',
                            borderRadius: '4px',
                            padding: '8px 16px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Reset
                        </button>
                        <button
                          className={`apply-btn ${(!startDate || !endDate) ? 'disabled' : ''}`}
                          onClick={handleDateRangeApply}
                          disabled={!startDate || !endDate}
                          style={{
                            background: (!startDate || !endDate) ? 'rgba(74, 84, 99, 0.5)' : '#4a5463',
                            border: '1px solid #4a5463',
                            color: (!startDate || !endDate) ? 'rgba(224, 224, 224, 0.5)' : '#e0e0e0',
                            borderRadius: '4px',
                            padding: '8px 16px',
                            cursor: (!startDate || !endDate) ? 'not-allowed' : 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Apply Custom Range
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="widget-content">
          {/* Loading state */}
          {loading && (
            <div className="loading-state" style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '250px',
              color: '#bfc9db',
              fontSize: '14px'
            }}>
              Loading connectivity data...
            </div>
          )}

          {/* No data state */}
          {!loading && connectivityData.length === 0 && (
            <div className="no-data-state" style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '250px',
              color: '#bfc9db',
              fontSize: '14px'
            }}>
              No data found for selected date range
            </div>
          )}

          {/* Chart - always rendered but hidden when loading or no data */}
          <div
            ref={chartDiv}
            style={{
              width: "100%",
              height: "250px",
              display: (loading || connectivityData.length === 0) ? 'none' : 'block'
            }}
          ></div>
        </div>
      </div>
    </>
  );
};

export default AssetSummaryDetails;
